import { api } from "@breakout/backend/convex/_generated/api"
import { type ChainConfig, getChainConfig, isSupportedChainId } from "@breakout/sdk"
import { ConvexQueryClient } from "@convex-dev/react-query"
import { PrivyProvider, usePrivy, useWallets } from "@privy-io/react-auth"
import { toSolanaWalletConnectors, useSolanaFundingPlugin } from "@privy-io/react-auth/solana"
import * as Sentry from "@sentry/react"
import { createSolanaRpc, createSolanaRpcSubscriptions } from "@solana/kit"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { ConvexProviderWithAuth, ConvexReactClient, useAction, useConvexAuth } from "convex/react"
import type { ComponentProps, ReactNode } from "react"
import { createContext, useContext, useEffect, useMemo, useRef } from "react"
import type { Chain, PublicClient } from "viem"
import { createPublicClient, http } from "viem"
import { base, baseSepolia, foundry } from "viem/chains"
import {
	E2E_MODE,
	VITE_CHAIN_ID,
	VITE_CONVEX_URL,
	VITE_PRIVY_APP_ID,
	VITE_RPC_URL,
	VITE_SOLANA_RPC_URL,
} from "./env"
import { clearSentryUser, setSentryUser } from "./sentry"

// =============================================================================
// Solana Wallet Connectors (for cross-chain bridging)
// =============================================================================

const solanaConnectors = toSolanaWalletConnectors({
	// Don't auto-connect - only connect when user explicitly chooses Solana funding
	// This prevents "no solana accounts found" errors for users without Solana wallets
	shouldAutoConnect: false,
})

// =============================================================================
// Environment Validation
// =============================================================================

// In E2E mode, Convex is optional (we use mock data or direct contract calls)
if (!VITE_CONVEX_URL && !E2E_MODE) {
	throw new Error("VITE_CONVEX_URL is required (unless in E2E mode)")
}

if (!VITE_PRIVY_APP_ID) {
	throw new Error("VITE_PRIVY_APP_ID is required")
}

// =============================================================================
// Convex Client
// =============================================================================

// Use a placeholder URL in E2E mode if Convex URL is not set
const convexClient = new ConvexReactClient(VITE_CONVEX_URL || "https://placeholder.convex.cloud")

// Create ConvexQueryClient for TanStack Query integration
const convexQueryClient = new ConvexQueryClient(convexClient)

// =============================================================================
// Blockchain Configuration
// =============================================================================

function getChain(): Chain {
	switch (VITE_CHAIN_ID) {
		case "8453":
			return base
		case "84532":
			return baseSepolia
		case "31337":
			return foundry
		default:
			return baseSepolia
	}
}

const chainId = VITE_CHAIN_ID ? Number.parseInt(VITE_CHAIN_ID, 10) : 84532
if (!isSupportedChainId(chainId)) {
	throw new Error(`Unsupported chain ID: ${chainId}`)
}
const chainConfig = getChainConfig(chainId)

const chain = getChain()
const publicClient = createPublicClient({
	chain,
	transport: http(VITE_RPC_URL || undefined, {
		retryCount: 3,
		retryDelay: 150,
		timeout: 5_000, // 5s timeout (reduced from 10s default for faster failure)
	}),
	// Enable automatic multicall batching for parallel reads
	batch: {
		multicall: {
			batchSize: 256, // Max calls per batch
			wait: 10, // Wait 10ms to batch calls
		},
	},
})
export const breakoutChain = chain
export { chainConfig }

// =============================================================================
// SDK Config Context
// =============================================================================

/**
 * Extended config that includes the public client for SDK operations
 */
export interface BreakoutAppConfig {
	/** Chain ID (from VITE_CHAIN_ID env var) */
	chainId: number
	/** Chain configuration from SDK */
	chainConfig: ChainConfig
	/** Viem public client for chain reads */
	publicClient: PublicClient
}

const BreakoutConfigContext = createContext<BreakoutAppConfig | null>(null)

export function useBreakoutConfig(): BreakoutAppConfig {
	const context = useContext(BreakoutConfigContext)
	if (!context) {
		throw new Error("useBreakoutConfig must be used within Providers")
	}
	return context
}

// =============================================================================
// Query Client
// =============================================================================

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			queryKeyHashFn: convexQueryClient.hashFn(),
			queryFn: convexQueryClient.queryFn(),
			// Better defaults for caching and retries
			staleTime: 30_000, // 30s default stale time
			gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
			retry: (failureCount, error) => {
				// Don't retry auth or validation errors
				if (error instanceof Error) {
					const msg = error.message.toLowerCase()
					if (msg.includes("auth") || msg.includes("unauthorized") || msg.includes("forbidden")) {
						return false
					}
				}
				return failureCount < 3
			},
			refetchOnWindowFocus: false, // Prevent aggressive refetching
		},
		mutations: {
			retry: false, // Never retry mutations - especially transactions
		},
	},
})
convexQueryClient.connect(queryClient)

// =============================================================================
// Convex Auth Hook (bridges Privy to Convex)
// =============================================================================

type ConvexAuthHook = ReturnType<ComponentProps<typeof ConvexProviderWithAuth>["useAuth"]>

function useAuthFromPrivy(): ConvexAuthHook {
	const { ready, user, getAccessToken } = usePrivy()

	return useMemo(
		() => ({
			isLoading: !ready,
			isAuthenticated: ready && !!user,
			fetchAccessToken: async () => {
				try {
					return await getAccessToken()
				} catch {
					return null
				}
			},
		}),
		[ready, user, getAccessToken],
	)
}

// =============================================================================
// User Sync Component (syncs Privy user to Convex backend on login)
// =============================================================================

function UserSync() {
	const { isLoading: isConvexAuthLoading, isAuthenticated: isPrivyAuthenticated } = useConvexAuth()
	const ensureOnLogin = useAction(api.domains.user.auth.actions.ensureOnLogin)

	// Track synced user to avoid duplicate calls
	const syncedRef = useRef<boolean>(false)

	useEffect(() => {
		// Wait for Convex auth to be ready
		if (isConvexAuthLoading) {
			return
		}

		if (!isPrivyAuthenticated) {
			// Reset on logout
			syncedRef.current = false
			return
		}

		// Skip if already synced this session
		if (syncedRef.current) {
			return
		}

		let cancelled = false
		let attempt = 0
		const maxAttempts = 3
		const baseDelay = 2000

		async function syncUser() {
			while (attempt < maxAttempts && !cancelled) {
				attempt++
				try {
					const userId = await ensureOnLogin({})
					if (!cancelled && userId) {
						syncedRef.current = true
					}
					return
				} catch {
					if (cancelled) {
						return
					}

					if (attempt < maxAttempts) {
						const delay = baseDelay * 2 ** (attempt - 1)
						await new Promise(resolve => setTimeout(resolve, delay))
					}
				}
			}
		}

		syncUser().catch(err => {
			console.error("Failed to sync user:", err)

			// Log authentication sync failures to Sentry
			Sentry.captureException(err, {
				tags: { operation: "userSync", domain: "authentication" },
				extra: {
					attempt,
					maxAttempts,
				},
			})
		})

		return () => {
			cancelled = true
		}
	}, [isConvexAuthLoading, isPrivyAuthenticated, ensureOnLogin])

	return null
}

// =============================================================================
// Sentry User Sync (sets user context for error tracking)
// =============================================================================

function SentryUserSync() {
	const { ready, user, authenticated } = usePrivy()
	const { wallets } = useWallets()
	const sentryUserRef = useRef<string | null>(null)

	useEffect(() => {
		if (!ready) {
			return
		}

		if (!authenticated || !user) {
			// Clear Sentry user on logout
			if (sentryUserRef.current) {
				clearSentryUser()
				sentryUserRef.current = null
			}
			return
		}

		// Only update if user changed
		if (sentryUserRef.current === user.id) {
			return
		}

		// Find the user's primary EVM wallet
		const evmWallet = wallets.find(w => "chainType" in w && w.chainType === "ethereum")

		setSentryUser({
			id: user.id,
			email: user.email?.address || user.google?.email || null,
			walletAddress: evmWallet?.address || null,
			displayName: user.google?.name || null,
		})

		sentryUserRef.current = user.id
	}, [ready, authenticated, user, wallets])

	return null
}

// =============================================================================
// Solana Funding Plugin (enables cross-chain bridging from Solana to EVM)
// =============================================================================

function SolanaFundingPluginProvider({ children }: { children: ReactNode }) {
	useSolanaFundingPlugin()
	return <>{children}</>
}

// =============================================================================
// Main Providers
// =============================================================================

export function Providers({ children }: { children: ReactNode }) {
	// publicClient is already typed correctly from createPublicClient
	// The useMemo type annotation ensures type safety
	const breakoutConfig = useMemo<BreakoutAppConfig>(
		() => ({
			chainId,
			chainConfig,
			publicClient,
		}),
		[],
	)

	return (
		<PrivyProvider
			appId={VITE_PRIVY_APP_ID}
			config={{
				appearance: {
					theme: "dark",
					accentColor: "#6366f1",
					walletChainType: "ethereum-and-solana",
					landingHeader: "Welcome to Breakout",
					walletList: ["phantom", "detected_wallets"], // Enable Phantom + other detected wallets for bridging
				},
				loginMethods: ["google", "email"],
				defaultChain: chain,
				supportedChains: [chain],
				embeddedWallets: {
					ethereum: {
						createOnLogin: "users-without-wallets",
					},
					// Silent signing - no confirmation popups for embedded wallet signatures
					// Users just click buttons, signing happens automatically
					showWalletUIs: false,
				},
				externalWallets: {
					solana: {
						connectors: solanaConnectors,
					},
				},
				// Solana RPC configuration for cross-chain bridging
				...(VITE_SOLANA_RPC_URL && {
					solana: {
						rpcs: {
							"solana:mainnet": {
								rpc: createSolanaRpc(VITE_SOLANA_RPC_URL),
								rpcSubscriptions: createSolanaRpcSubscriptions(
									VITE_SOLANA_RPC_URL.replace("https://", "wss://"),
								),
							},
						},
					},
				}),
			}}
		>
			<SolanaFundingPluginProvider>
				<ConvexProviderWithAuth client={convexClient} useAuth={useAuthFromPrivy}>
					<UserSync />
					<SentryUserSync />
					<QueryClientProvider client={queryClient}>
						<BreakoutConfigContext.Provider value={breakoutConfig}>
							{children}
						</BreakoutConfigContext.Provider>
						<ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
					</QueryClientProvider>
				</ConvexProviderWithAuth>
			</SolanaFundingPluginProvider>
		</PrivyProvider>
	)
}
